import { delay } from 'redux-saga'
import { all, takeLatest, take, call, put, race, fork } from 'redux-saga/effects'
import injectSaga from '../../state/hoc/injectSaga'

import { loginRoutine } from './routines'
import * as actions from './actions'
import api from './api'

const {
  SET_TOKEN,
  ON_TOKEN_EXPIRED,
  LOGOUT,
  START_EXPIRES_TIMER,
  CANCEL_EXPIRES_TIMER
} = actions

export function * authorize(action) {
  try {
    yield put(loginRoutine.request())
    const { username, password } = action.payload
    const token = yield call(api.login, username, password)

    yield put(loginRoutine.success(token))
    return token
  } catch (error) {
    yield put(loginRoutine.failure(error))
  } finally {
    yield put(loginRoutine.fulfill())
  }
}

export function * sessionExpiresTimer(expiresIn) {
  const timeLeft = (expiresIn * 1000) - Date.now()

  yield call(delay, timeLeft)
  yield put(actions.onTokenExpired())
}

export function * cancelSessionExpiresTimer() {
  while (true) {
    yield take(CANCEL_EXPIRES_TIMER)
    return
  }
}

export function * startSessionExpiresTimer(action) {
  const expiresIn = action.payload

  yield race({
    task: call(sessionExpiresTimer, expiresIn),
    cancel: call(cancelSessionExpiresTimer)
  })
}

export function * setToken(action) {
  const token = action.payload
  yield call(api.storeToken, token)

  // set flag to redirect to login or to register when user is not logged in
  api.storeHasUser()

  const { exp } = api.decodeToken(token)
  yield put(actions.startExpiresTimer(exp))
}

export function * removeToken() {
  yield call(api.removeToken)
  yield put(actions.cancelExpiresTimer())
}

export function * initFromSession() {
  const token = yield call(api.retrieveToken)

  if (!token) return

  if (!api.isTokenExpired(token)) {
    yield put(actions.setToken(token))
  } else {
    yield put(actions.onTokenExpired())
  }
}

export function * loginFlow(action) {
  const winner = yield race({
    token: call(authorize, action),
    logout: take([LOGOUT, loginRoutine.FAILURE])
  })

  if (winner.token) {
    yield put(actions.setToken(winner.token))
  }
}

export function * listenActions() {
  yield all([
    takeLatest(loginRoutine.TRIGGER, loginFlow),
    takeLatest(SET_TOKEN, setToken),
    takeLatest([LOGOUT, ON_TOKEN_EXPIRED], removeToken),
    takeLatest(START_EXPIRES_TIMER, startSessionExpiresTimer)
  ])
}

export function * loginSaga() {
  yield all([
    fork(listenActions),
    fork(initFromSession)
  ])
}

export const withSaga = injectSaga({ key: 'login', saga: loginSaga })
